<template>
  <div class="color-travel-map-container" @click="handleClose">
    <transition name="map-fade" appear>
      <div class="map-content-wrapper" @click.stop>
        <div class="map-header">
          <div class="color-info">
            <div class="swatch" :style="{ backgroundColor: colorData.hex }"></div>
            <span>{{ colorData.name }} - 色彩游线</span>
          </div>
          <button class="close-button" @click="handleClose" title="关闭地图">×</button>
        </div>

        <div class="map-view">
          <svg class="terrain-svg" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
            <path
              v-for="(line, index) in terrainLines"
              :key="'terrain-' + index"
              :d="line.d"
              class="terrain-line"
              :style="{ animationDelay: `${index * 0.05}s` }"
            />
            <path
              :d="travelPath"
              class="travel-path"
              :stroke="colorData.hex"
            />
          </svg>
        </div>

        <div class="map-footer">
          <p>在 {{ colorData.element.location.name }} 探索 {{ colorData.name }} 的色彩轨迹。</p>
          <div class="stats">
            <span>海拔: {{ colorData.element.altitude }}m</span>
            <span>经纬度: {{ colorData.element.location.lng.toFixed(4) }}, {{ colorData.element.location.lat.toFixed(4) }}</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted } from 'vue';

// 假设通过 props 接收来自 Pinia store 或路由的颜色数据
const props = defineProps({
  colorData: {
    type: Object,
    required: true,
    // 提供一个默认的示例对象结构，以便组件独立测试
    default: () => ({
      hex: '#D9A42E',
      name: '金色阳光',
      element: {
        name: '贡嘎山日出',
        altitude: 7556,
        location: {
          name: '贡嘎山',
          lng: 101.88,
          lat: 29.59,
        }
      }
    })
  }
});

const emit = defineEmits(['close']);

const handleClose = () => {
  emit('close');
};

// --- SVG 数据 ---
// 预设的地形等高线数据 (这些路径可以从设计工具导出或程序生成)
const terrainLines = ref([
  { d: "M-50,150 Q200,50 400,180 T700,150 L850,200" },
  { d: "M-50,220 Q150,120 350,250 T650,220 L850,280" },
  { d: "M-50,300 Q250,200 450,330 T750,300 L850,350" },
  { d: "M-50,380 Q200,280 400,410 T700,380 L850,430" },
  { d: "M-50,460 Q300,360 500,490 T800,460 L850,510" },
  { d: "M-50,540 Q250,440 450,570 T750,540 L850,590" }
]);

// 预设的旅行路径数据
const travelPath = ref("M100,500 C 250,350 350,200 500,220 S 650,300 700,450");

</script>

<style scoped>
.color-travel-map-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: pointer;
}

.map-content-wrapper {
  width: 90%;
  max-width: 880px;
  height: auto;
  background-color: #1a1a1a;
  color: #e0e0e0;
  border-radius: 16px;
  border: 1px solid #333;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: default;
}

.map-header {
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #333;
  flex-shrink: 0;
}

.color-info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 500;
}

.swatch {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 1px solid #555;
}

.close-button {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #888;
  border-radius: 50%;
  font-size: 24px;
  line-height: 32px;
  text-align: center;
  cursor: pointer;
  transition: all .2s ease;
}
.close-button:hover {
  transform: rotate(90deg);
  background: #333;
  color: #fff;
}

.map-view {
  width: 100%;
  padding: 16px 0;
  background: radial-gradient(circle, #2a2a2a 0%, #1a1a1a 70%);
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.terrain-svg {
  width: 100%;
  height: 100%;
  max-height: 60vh;
}

.terrain-line {
  fill: none;
  stroke: #444;
  stroke-width: 1.5;
  opacity: 0;
  animation: draw-in 1s ease-out forwards;
}

.travel-path {
  fill: none;
  stroke-width: 4;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 0 10px var(--path-glow-color));
  --path-glow-color: v-bind('props.colorData.hex + "80"'); /* 动态设置阴影颜色 */

  /* 路径绘制动画 */
  stroke-dasharray: 1000;
  stroke-dashoffset: 1000;
  animation: draw-path 2s 0.5s ease-in-out forwards;
}

.map-footer {
  padding: 16px 24px;
  border-top: 1px solid #333;
  font-size: 14px;
  color: #aaa;
  flex-shrink: 0;
}
.map-footer p {
  margin: 0 0 8px 0;
}
.stats {
  display: flex;
  gap: 24px;
  font-family: monospace;
}


/* --- Vue 过渡动画 --- */
.map-fade-enter-active,
.map-fade-leave-active {
  transition: opacity 0.4s ease;
}
.map-fade-enter-active .map-content-wrapper,
.map-fade-leave-active .map-content-wrapper {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.map-fade-enter-from,
.map-fade-leave-to {
  opacity: 0;
}
.map-fade-enter-from .map-content-wrapper,
.map-fade-leave-to .map-content-wrapper {
  transform: scale(0.95) translateY(20px);
  opacity: 0;
}

/* --- SVG 动画 Keyframes --- */
@keyframes draw-in {
  to {
    opacity: 1;
  }
}

@keyframes draw-path {
  to {
    stroke-dashoffset: 0;
  }
}
</style>